import React from 'react';

interface TestExecutionControlsProps {
  selectedScenarios: string[];
  executionStatus: 'idle' | 'running' | 'completed' | 'failed';
  onExecute: () => void;
  onStop: () => void;
  onReset: () => void;
}

export const TestExecutionControls: React.FC<TestExecutionControlsProps> = ({
  selectedScenarios,
  executionStatus,
  onExecute,
  onStop,
  onReset
}) => {
  const isRunning = executionStatus === 'running';
  const hasSelection = selectedScenarios.length > 0;
  const hasResults = executionStatus === 'completed' || executionStatus === 'failed';

  return (
    <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
        Test Execution
      </h2>

      {/* Status Indicator */}
      <div className="mb-6">
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${
            isRunning ? 'bg-yellow-500 animate-pulse' :
            executionStatus === 'completed' ? 'bg-green-500' :
            executionStatus === 'failed' ? 'bg-red-500' :
            'bg-gray-300 dark:bg-gray-600'
          }`} />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {isRunning ? 'Tests Running...' :
             executionStatus === 'completed' ? 'Tests Completed' :
             executionStatus === 'failed' ? 'Tests Failed' :
             'Ready to Execute'}
          </span>
        </div>
      </div>

      {/* Control Buttons */}
      <div className="space-y-3">
        {/* Execute Button */}
        <button
          onClick={onExecute}
          disabled={!hasSelection || isRunning}
          className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
            !hasSelection || isRunning
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
          }`}
        >
          {isRunning ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              Executing Tests...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m6-5a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Execute Selected Tests ({selectedScenarios.length})
            </>
          )}
        </button>

        {/* Stop Button */}
        {isRunning && (
          <button
            onClick={onStop}
            className="w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium text-sm bg-red-600 hover:bg-red-700 text-white transition-all duration-200"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10h6v4H9z" />
            </svg>
            Stop Execution
          </button>
        )}

        {/* Reset Button */}
        {hasResults && !isRunning && (
          <button
            onClick={onReset}
            className="w-full flex items-center justify-center px-4 py-2 rounded-lg font-medium text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reset Tests
          </button>
        )}
      </div>

      {/* Execution Info */}
      {hasSelection && !isRunning && (
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <div className="font-medium mb-1">Ready to execute:</div>
            <ul className="space-y-1 ml-4">
              <li>• {selectedScenarios.length} test scenario{selectedScenarios.length !== 1 ? 's' : ''}</li>
              <li>• Estimated duration: ~{Math.ceil(selectedScenarios.length * 3)} minutes</li>
              <li>• Screenshots and logs will be captured</li>
            </ul>
          </div>
        </div>
      )}

      {/* No Selection Warning */}
      {!hasSelection && !isRunning && (
        <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
          <div className="flex items-center text-sm text-yellow-800 dark:text-yellow-300">
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            Please select at least one test scenario to execute
          </div>
        </div>
      )}
    </div>
  );
};